import { USDV_MINT } from "@shared/tokens/usdv";
import {
    ParticipantAccountStatus,
    type WalletParticipantAccount,
    type YaasPosition,
    type YaasPositionProgram,
    type YaasPositionSplit,
    type YaasProgramLpPosition,
    type YaasWalletHolding,
    type YaasWalletObservation,
    type YaasWalletResponse
} from "@solomon-apps/shared/src/types/yaas";

import { isRecord } from "./verificationResponse";
import { isHoldingObservation, isLpHolding, isLpIdentity } from "./yaasLpHoldings";

const isNullableString = (value: unknown): value is string | null => value === null || typeof value === "string";
const isDecimal = (value: unknown): value is string => typeof value === "string" && /^\d+(\.\d+)?$/.test(value);
const isDate = (value: unknown): value is string => typeof value === "string" && Number.isFinite(Date.parse(value));
const isAtomic = (value: unknown): value is string => typeof value === "string" && /^\d+$/.test(value);
const isStrings = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every((item: unknown) => typeof item === "string");

function isAccount(value: unknown, address: string): value is WalletParticipantAccount | null {
    return (
        value === null ||
        (isRecord(value) &&
            value.address === address &&
            typeof value.uuid === "string" &&
            (value.label === undefined || isNullableString(value.label)) &&
            isNullableString(value.rewardsDestinationAddress) &&
            Object.values(ParticipantAccountStatus).some((status) => status === value.status) &&
            (value.distributionMode === null ||
                value.distributionMode === "stream" ||
                value.distributionMode === "claim") &&
            (value.isRewardsEligible === undefined || typeof value.isRewardsEligible === "boolean") &&
            isDecimal(value.totalPositionsValue) &&
            isDecimal(value.totalEarnedRewards) &&
            (value.historicalAprPct === undefined ||
                value.historicalAprPct === null ||
                isDecimal(value.historicalAprPct)) &&
            isDate(value.createdAt) &&
            isDate(value.updatedAt))
    );
}

function isProgram(value: unknown): value is YaasPositionProgram {
    return (
        isRecord(value) &&
        typeof value.uuid === "string" &&
        typeof value.name === "string" &&
        ["Draft", "Active", "Paused", "Ended"].some((status) => status === value.status) &&
        (value.participationMode === "automatic" || value.participationMode === "participant_opt_in")
    );
}

function isSplit(value: unknown): value is YaasPositionSplit {
    return (
        isRecord(value) &&
        typeof value.savedDestinationUuid === "string" &&
        typeof value.name === "string" &&
        typeof value.address === "string" &&
        typeof value.percentageBps === "number" &&
        Number.isInteger(value.percentageBps) &&
        value.percentageBps >= 0 &&
        value.percentageBps <= 10_000
    );
}

const LP_OBSERVATION_KEYS = ["tokenX", "tokenY", "amountX", "amountY", "solanaSlot", "observedAt", "fetchedAt"];

/** yaas-api sends an LP's last observation whole, or not at all before the first one. */
function isProgramLpPosition(value: unknown): value is YaasProgramLpPosition {
    if (!isRecord(value)) return false;
    const observed = LP_OBSERVATION_KEYS.every((key) => value[key] === undefined)
        ? true
        : typeof value.tokenX === "string" &&
          typeof value.tokenY === "string" &&
          isAtomic(value.amountX) &&
          isAtomic(value.amountY) &&
          (value.solanaSlot === null || isAtomic(value.solanaSlot)) &&
          (value.observedAt === null || isDate(value.observedAt)) &&
          isDate(value.fetchedAt);
    return observed && isLpIdentity(value);
}

function isPosition(value: unknown): value is YaasPosition {
    if (!isRecord(value) || !isRecord(value.surface)) return false;
    const surface = value.surface;
    return (
        typeof value.id === "number" &&
        Number.isSafeInteger(value.id) &&
        typeof value.uuid === "string" &&
        isNullableString(value.holdingId) &&
        isNullableString(surface.id) &&
        typeof surface.name === "string" &&
        typeof surface.displayName === "string" &&
        ["DEX_LP", "TREASURY", "NEOBANK", "LENDING"].some((kind) => kind === surface.displayType) &&
        typeof surface.address === "string" &&
        typeof surface.providerName === "string" &&
        isNullableString(surface.category) &&
        isNullableString(surface.type) &&
        isDecimal(value.amount) &&
        isDecimal(value.totalRewards) &&
        isNullableString(value.status) &&
        isNullableString(value.rewardsDestinationAddress) &&
        (value.lpPosition === undefined || isProgramLpPosition(value.lpPosition)) &&
        (value.splits === undefined || (Array.isArray(value.splits) && value.splits.every(isSplit))) &&
        (value.enrollmentSource === undefined ||
            value.enrollmentSource === null ||
            value.enrollmentSource === "automatic" ||
            value.enrollmentSource === "participant_opt_in") &&
        (value.program === undefined || isProgram(value.program)) &&
        isDate(value.createdAt) &&
        isDate(value.updatedAt)
    );
}

function isWalletHolding(value: unknown): value is YaasWalletHolding {
    return (
        isRecord(value) &&
        typeof value.id === "string" &&
        typeof value.ownerAddress === "string" &&
        isDate(value.fetchedAt) &&
        (value.kind === "token"
            ? value.assetMint === USDV_MINT && isHoldingObservation(value)
            : value.kind === "amm-position" && isLpHolding(value))
    );
}

function isObservation(value: unknown): value is YaasWalletObservation {
    return (
        isRecord(value) &&
        (value.status === "partial" || value.status === "unavailable") &&
        value.source === "jachin" &&
        isNullableString(value.ownerAddress) &&
        value.complete === false &&
        typeof value.lpComplete === "boolean" &&
        (value.reason === "owner_enumeration_unavailable" || value.reason === "discovery_unavailable") &&
        (value.fetchedAt === null || isDate(value.fetchedAt)) &&
        isNullableString(value.configVersion) &&
        isStrings(value.poolAddresses) &&
        isStrings(value.assetMints)
    );
}

/** Keeps the rows that pass, so one malformed row cannot blank the whole wallet. */
/** Rows already reported, so the wallet view's 10-second refetch does not repeat a warning. */
const reportedRows = new Set<string>();

function validRows<T>(rows: unknown[], isValid: (row: unknown) => row is T, kind: string): T[] {
    return rows.filter((row): row is T => {
        if (isValid(row)) return true;
        const key = `${kind}:${JSON.stringify(row)}`;
        if (!reportedRows.has(key)) {
            reportedRows.add(key);
            console.warn(`Dropped a malformed wallet ${kind}`, row);
        }
        return false;
    });
}

/**
 * Untrusted JSON is narrowed here; consumers only receive the checked wallet contract.
 * The envelope must be valid. A malformed position or holding is dropped on its own:
 * a dropped holding reads as one the scan omitted, which never proves ownership loss.
 */
export function parseWalletViewResponse(value: unknown, address: string): YaasWalletResponse | null {
    if (
        !isRecord(value) ||
        !isObservation(value.observation) ||
        value.walletAddress !== address ||
        !isAccount(value.account, address) ||
        !Array.isArray(value.programPositions) ||
        !Array.isArray(value.holdings)
    )
        return null;
    const observation = value.observation;
    return {
        walletAddress: address,
        account: value.account,
        programPositions: validRows(value.programPositions, isPosition, "position"),
        holdings: validRows(
            value.holdings,
            (row): row is YaasWalletHolding => isWalletHolding(row) && row.ownerAddress === observation.ownerAddress,
            "holding"
        ),
        observation
    };
}
